// The team table's row model and the presentation helpers it shares with the
// card grid. Both views render the same teams, so the avatar palette lives
// here rather than in either view — a team that is teal as a card has to stay
// teal as a row, or the toggle looks like it changed the data.

import type { TeamRef } from '../../../services/idp/catalogService'
import type { Incident } from '../../../services/idp/incidentsService'
import type { TeamStats } from '../../../types/quality-gates'

/**
 * One row of the team table.
 *
 * Everything sortable is a scalar on the row because DataTable sorts with a
 * bare `a[field] > b[field]` — it cannot reach into the `_`-prefixed originals,
 * which are carried alongside purely for the cell slots to render from.
 */
export interface TeamRow {
  key: string
  name: string
  initials: string
  serviceCount: number
  subTeamCount: number
  incidentCount: number
  /**
   * -1 for a team with no scorecard, rendered as an em dash. `undefined` would
   * compare false against every number in that naive sort, so rows without a
   * rate would land wherever the input order happened to put them.
   */
  passRate: number
  _team: TeamRef
  _incidents: Incident[]
  _scorecard: TeamStats | null
}

/** A team with no scorecard, kept out of the "below 80%" filter and the bar. */
export const NO_PASS_RATE = -1

// Cycled by a hash of the team key so each team keeps a stable, distinct
// color across reloads without needing a color assigned server-side.
const AVATAR_PALETTE = [
  { bg: 'bg-indigo-100 dark:bg-indigo-500/15', text: 'text-indigo-600 dark:text-indigo-300' },
  { bg: 'bg-violet-100 dark:bg-violet-500/15', text: 'text-violet-600 dark:text-violet-300' },
  { bg: 'bg-teal-100 dark:bg-teal-500/15', text: 'text-teal-600 dark:text-teal-300' },
  { bg: 'bg-sky-100 dark:bg-sky-500/15', text: 'text-sky-600 dark:text-sky-300' },
  { bg: 'bg-emerald-100 dark:bg-emerald-500/15', text: 'text-emerald-600 dark:text-emerald-300' },
  { bg: 'bg-fuchsia-100 dark:bg-fuchsia-500/15', text: 'text-fuchsia-600 dark:text-fuchsia-300' },
  { bg: 'bg-cyan-100 dark:bg-cyan-500/15', text: 'text-cyan-600 dark:text-cyan-300' },
  { bg: 'bg-blue-100 dark:bg-blue-500/15', text: 'text-blue-600 dark:text-blue-300' }
]

export interface AvatarColor {
  bg: string
  text: string
}

function hashKey(key: string): number {
  let h = 0
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0
  return h
}

export function avatarColorFor(key: string): AvatarColor {
  return AVATAR_PALETTE[hashKey(key) % AVATAR_PALETTE.length]
}

export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

/** ClickUp stores "CRM"; the catalog key is "crm". */
export function foldTeamKey(s: string): string {
  return s.toLowerCase().replace(/[\s_-]+/g, '')
}

export function incidentMatchesTeam(team: string | null, key: string, name?: string): boolean {
  if (!team) return false
  const a = foldTeamKey(team)
  if (a === foldTeamKey(key)) return true
  return !!name && a === foldTeamKey(name)
}

export type QuickFilterId = 'incidents' | 'lowPassRate' | 'noSubTeams'

/**
 * The quick filter chips, defined once so the live counts on the chips and the
 * filtering itself can never disagree about what a chip means.
 */
export const QUICK_FILTERS: {
  id: QuickFilterId
  label: string
  match: (row: TeamRow) => boolean
}[] = [
  { id: 'incidents', label: 'Has open incidents', match: (row) => row.incidentCount > 0 },
  {
    id: 'lowPassRate',
    label: 'Pass rate < 80%',
    match: (row) => row.passRate !== NO_PASS_RATE && row.passRate < 80
  },
  { id: 'noSubTeams', label: 'No sub-teams', match: (row) => row.subTeamCount === 0 }
]
